import { useEffect, useState } from "react";
import type { Section } from "../api/articleUnits";

/**
 * `canvasSectionRegions` — the SECTION ⇄ GEOMETRY map behind the margin hover
 * handle, on the canvas.
 *
 * It replaces `lib/sectionRegions`, which walked `.docz-block` elements of the
 * painted DOM copy and anchored each grip to a block's `offsetTop`. Those
 * elements are gone.
 *
 * THIS FEATURE WAS ALREADY DEAD AND SAID SO. `readCanvas.css` hid the DOM copy
 * with `display: none`, and an element with no box has `offsetTop === 0` — so
 * every grip stacked at the top of the paper, and the stylesheet's own comment
 * admitted it: "the section hover handles do not appear in canvas mode … a REAL,
 * open gap on the canvas reader". Deleting the DOM path without rebuilding this
 * would have made a dead feature permanent instead of fixing it.
 *
 * WHAT A CANVAS CAN ANSWER. There is no element per block, but there IS geometry:
 * the surface maps a stored `block_ref` to the viewport rect of that block
 * (`ReadCanvasHandle.rectForBlockRef`). A section therefore becomes a `top` in the
 * positioned paper's own coordinates plus the vertical BAND it occupies, which is
 * everything the layer needs: the band places the grip and answers "is the pointer
 * over this section" without any per-block hit-testing.
 *
 * A section runs from its own top to the NEXT placed section's top — the same
 * "start element to the next section's start element" rule the DOM version used,
 * expressed in pixels instead of nodes.
 */

/** One section's placement on the painted surface, in paper-relative pixels. */
export interface CanvasSectionRegion {
  readonly section: Section;
  /** Distance from the top of the positioned paper to the section's first block. */
  readonly top: number;
  /** Where the section's band ends — the next placed section's `top`, or the
   *  bottom of the measured content for the last one. */
  readonly bottom: number;
}

/** The surface capability this module needs — structural, so it never imports
 *  `<ReadCanvas>` and stays testable with a plain stand-in. */
export interface BlockRectSource {
  rectForBlockRef(blockRef: string): DOMRect | null;
}

/**
 * Place every section that the surface can locate, in document order.
 *
 * `containerTop` is the viewport Y of the positioned paper, subtracted so the
 * result is what an absolutely-positioned grip can use directly.
 *
 * A section whose `anchor_key` names no block is DROPPED rather than placed at
 * zero. That is the whole lesson of the defect this replaces: a handle at a
 * position nobody chose is worse than no handle, because it looks placed.
 */
export function computeCanvasSectionRegions(
  source: BlockRectSource,
  sections: readonly Section[],
  containerTop: number,
): CanvasSectionRegion[] {
  const placed: { section: Section; top: number; height: number }[] = [];
  for (const section of sections) {
    const rect = source.rectForBlockRef(section.anchor_key);
    if (!rect) continue;
    placed.push({
      section,
      top: rect.top - containerTop,
      height: rect.height,
    });
  }
  // Document order by measured position, not by the order the sections arrived:
  // the surface is the authority on where things actually ended up.
  placed.sort((a, b) => a.top - b.top);
  return placed.map((p, i) => ({
    section: p.section,
    top: p.top,
    // Up to the next section, or this block's own extent for the last one.
    bottom: placed[i + 1]?.top ?? p.top + p.height,
  }));
}

/** Which section's band contains `y` (paper-relative), or `null`. */
export function sectionOrdAt(
  regions: readonly CanvasSectionRegion[],
  y: number,
): number | null {
  for (const r of regions) {
    if (y >= r.top && y < r.bottom) return r.section.ord;
  }
  return null;
}

/**
 * Recompute the placement whenever the surface repaints.
 *
 * `paintKey` is the caller's "the document changed" signal and `revision` its
 * "the surface relaid out" one; both are dependencies because a canvas relayout
 * moves every block without changing the section list. The measurement is
 * deferred a frame so it runs after the surface has painted — asking a canvas for
 * geometry it has not laid out yet returns rects for the previous layout.
 */
export function useCanvasSectionRegions(
  source: React.RefObject<BlockRectSource | null>,
  container: React.RefObject<HTMLElement | null>,
  sections: readonly Section[],
  active: boolean,
  paintKey: string,
): CanvasSectionRegion[] {
  const [regions, setRegions] = useState<CanvasSectionRegion[]>([]);

  useEffect(() => {
    if (!active) {
      setRegions([]);
      return;
    }
    let cancelled = false;
    const measure = (): void => {
      const src = source.current;
      const host = container.current;
      if (cancelled || !src || !host) return;
      setRegions(
        computeCanvasSectionRegions(
          src,
          sections,
          host.getBoundingClientRect().top,
        ),
      );
    };
    const raf = requestAnimationFrame(measure);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [source, container, sections, active, paintKey]);

  return regions;
}
